import { openReportWithAuth } from "../util";
import type { PaginatedResponse } from "./courseCertificates";
import { cleApi, getCleApiPrefix } from "./client";
import type {
  ReductionEvidenceListModel,
  ReductionEvidenceModel,
} from "./models/reductions";
import axios from "axios";

export type {
  ReductionDocumentModel,
  ReductionEvidenceListModel,
  ReductionEvidenceModel,
} from "./models/reductions";

export interface GetReductionsParams {
  userId: string;
  page?: number;
  pageSize?: number;
  reported?: boolean;
}

export interface GetReductionByIdParams {
  userId: string;
  reductionId: string;
}

export interface GetReductionsByPeriodParams {
  userId: string;
  periodId: string;
}

export interface GetReductionsByInjunctionParams {
  userId: string;
  injunctionId: string;
}

export interface GetReductionsByImpositionParams {
  userId: string;
  impositionId: string;
}

export interface UpdateReductionRequest {
  reason?: string | null;
  number_of_lessons?: number;
}

export interface UpdateReductionParams {
  userId: string;
  reductionId: string;
  body: UpdateReductionRequest;
}

export const getReductions = ({
  userId,
  page = 1,
  pageSize = 20,
  reported,
}: GetReductionsParams): Promise<
  PaginatedResponse<ReductionEvidenceListModel>
> => {
  const query = new URLSearchParams({
    page: String(page),
    page_size: String(pageSize),
  });

  if (reported !== undefined) {
    query.set("reported", String(reported));
  }

  return cleApi.get<PaginatedResponse<ReductionEvidenceListModel>>(
    `users/${encodeURIComponent(userId)}/reductions?${query.toString()}`,
  );
};

export const getReductionById = ({
  userId,
  reductionId,
}: GetReductionByIdParams): Promise<ReductionEvidenceModel> =>
  cleApi.get<ReductionEvidenceModel>(
    `users/${encodeURIComponent(userId)}/reductions/${encodeURIComponent(
      reductionId,
    )}`,
  );

export const updateReduction = ({
  userId,
  reductionId,
  body,
}: UpdateReductionParams): Promise<ReductionEvidenceModel> =>
  cleApi.put<ReductionEvidenceModel>(
    `users/${encodeURIComponent(userId)}/reductions/${encodeURIComponent(reductionId)}`,
    body,
  );

export const getReductionsByPeriodId = ({
  userId,
  periodId,
}: GetReductionsByPeriodParams): Promise<ReductionEvidenceListModel[]> =>
  cleApi.get<ReductionEvidenceListModel[]>(
    `users/${encodeURIComponent(userId)}/reductions/by-period/${encodeURIComponent(periodId)}`,
  );

export const getReductionsByInjunctionId = ({
  userId,
  injunctionId,
}: GetReductionsByInjunctionParams): Promise<ReductionEvidenceListModel[]> =>
  cleApi.get<ReductionEvidenceListModel[]>(
    `users/${encodeURIComponent(userId)}/reductions/by-injunction/${encodeURIComponent(injunctionId)}`,
  );

export const getReductionsByImpositionId = ({
  userId,
  impositionId,
}: GetReductionsByImpositionParams): Promise<ReductionEvidenceListModel[]> =>
  cleApi.get<ReductionEvidenceListModel[]>(
    `users/${encodeURIComponent(userId)}/reductions/by-imposition/${encodeURIComponent(impositionId)}`,
  );

export const openReductionDownload = ({
  userId,
  reductionId,
}: GetReductionByIdParams): Promise<void> =>
  openReportWithAuth(
    `${getCleApiPrefix()}users/${encodeURIComponent(
      userId,
    )}/reductions/${encodeURIComponent(reductionId)}/download`,
  );

export const getReductionPdfBlob = async ({
  userId,
  reductionId,
}: GetReductionByIdParams): Promise<Blob> => {
  const response = await axios.get<Blob>(
    `${getCleApiPrefix()}users/${encodeURIComponent(
      userId,
    )}/reductions/${encodeURIComponent(reductionId)}/download`,
    { responseType: "blob" },
  );

  return response.data;
};
